/**
 * GlobalView — Zone Detail Popup
 *
 * Overlay showing zone details: name, type, altitude range,
 * description, source. Draggable via header.
 * Reuses the AircraftPopup CSS module for consistent styling.
 */

import { useEffect, useRef } from 'react';
import { useGlobeStore } from '../../stores/globeStore';
import { useEntityStore } from '../../stores/entityStore';
import { useDraggable } from '../../hooks/useDraggable';
import { useIsMobile } from '../../hooks/useIsMobile';
import EntityBottomSheet from './EntityBottomSheet';
import type { ZoneProperties } from '@globalview/shared';
import styles from './AircraftPopup.module.css';

// ─── Zone Type Labels ────────────────────────────────────────────

const ZONE_TYPE_LABELS: Record<string, string> = {
  no_fly:     '🔴 NO-FLY ZONE',
  danger:     '🟠 DANGER ZONE',
  restricted: '🟡 RESTRICTED',
  incident:   '🟣 INCIDENT',
  strike:     '🔴 STRIKE ZONE',
  custom:     '🔵 CUSTOM',
};

const ZONE_TYPE_COLORS: Record<string, string> = {
  no_fly:     '#ff2222',
  danger:     '#ff6600',
  restricted: '#ffaa00',
  incident:   '#cc44ff',
  strike:     '#ff4466',
  custom:     '#00d4ff',
};

function fmtAlt(meters: number): string {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(1)} km`;
  }
  return `${Math.round(meters)} m`;
}

export default function ZonePopup() {
  const selectedEntityId = useGlobeStore((s) => s.selectedEntityId);
  const setSelectedEntity = useGlobeStore((s) => s.setSelectedEntity);
  const entity = useEntityStore((s) =>
    selectedEntityId ? s.entities.get(selectedEntityId) : undefined,
  );
  const isMobile = useIsMobile();

  const { style: dragStyle, onMouseDown, isDragging, reset } = useDraggable(296, 32);
  const prevEntityRef = useRef<string | null>(null);

  useEffect(() => {
    if (selectedEntityId && selectedEntityId !== prevEntityRef.current) {
      reset();
      prevEntityRef.current = selectedEntityId;
    }
  }, [selectedEntityId, reset]);

  useEffect(() => {
    if (!selectedEntityId) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedEntity(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEntityId, setSelectedEntity]);

  if (!selectedEntityId || !entity || entity.type !== 'zone') return null;

  const props = entity.properties as unknown as ZoneProperties;
  const name = props?.name ?? entity.id;
  const zoneType = props?.zoneType ?? 'custom';
  const typeLabel = ZONE_TYPE_LABELS[zoneType] || '🟡 ZONE';
  const typeColor = ZONE_TYPE_COLORS[zoneType] || '#ffaa00';
  const description = props?.description ?? '';
  const upperAlt = props?.upperAlt ?? 0;
  const lowerAlt = props?.lowerAlt ?? 0;
  const source = entity.source || 'Unknown';

  const handleClose = () => setSelectedEntity(null);

  const zoneContent = (
    <>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          <span style={{ color: typeColor }}>{typeLabel}</span>
        </div>
      </div>

      {description && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>BESCHREIBUNG</div>
          <p style={{ 
            color: 'var(--text-dim)', 
            fontSize: '0.78rem', 
            lineHeight: '1.4',
            margin: '4px 0 0 0',
          }}>
            {description}
          </p>
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionTitle}>HÖHENBESCHRÄNKUNG</div>
        <div className={styles.grid}>
          <Row label="OBEN" value={fmtAlt(upperAlt)} />
          <Row label="UNTEN" value={lowerAlt === 0 ? 'GND' : fmtAlt(lowerAlt)} />
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>POSITION</div>
        <div className={styles.grid}>
          <Row label="LAT" value={entity.position.lat.toFixed(4)} suffix={entity.position.lat >= 0 ? '° N' : '° S'} />
          <Row label="LON" value={entity.position.lon.toFixed(4)} suffix={entity.position.lon >= 0 ? '° E' : '° W'} />
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>QUELLE</div>
        <div className={styles.grid}>
          <Row label="SRC" value={source} />
        </div>
      </div>
    </>
  );

  const desktopPopup = (
    <div
      className={styles.popup}
      style={dragStyle}
      id="zone-popup"
      data-draggable
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className={styles.header}
        onMouseDown={onMouseDown}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div className={styles.headerLeft}>
          <span className={styles.callsign}>🛡️ {name}</span>
        </div>
        <button className={styles.closeBtn} onClick={handleClose} title="Schliessen (ESC)" id="zone-popup-close">✕</button>
      </div>
      {zoneContent}
    </div>
  );

  if (isMobile) {
    return (
      <EntityBottomSheet
        visible={true}
        entityName={name}
        entitySub={typeLabel}
        icon="🛡️"
        onClose={handleClose}
        desktopPopup={desktopPopup}
      >
        {zoneContent}
      </EntityBottomSheet>
    );
  }

  return desktopPopup;
}

// ─── Row Helper ──────────────────────────────────────────────────

function Row({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>
        {value}{suffix && <span className={styles.suffix}>{suffix}</span>}
      </span>
    </div>
  );
}
